import React from 'react';

// Minimal segment + participant types shared across viewers
export type TranscriptSegment = { speaker: string; text: string };
export type TranscriptParticipant = { contactId?: string; name: string; avatar?: string | null };

// Map can be either a simple diarization label->assigned name map, or a SpeakerLinkMap
type SimpleAssignMap = Record<string, string | null | undefined>;
type SpeakerLinkMapEntry = { contactId: string; avatar: string | null; displayName: string };
type SpeakerLinkMap = Record<string, SpeakerLinkMapEntry>;

function resolveDisplay(speaker: string, map?: SimpleAssignMap | SpeakerLinkMap): string {
    if (!map) return speaker;
    const val: any = (map as any)[speaker];
    if (!val) return speaker;
    if (typeof val === 'string') return val || speaker;
    if (typeof val === 'object' && val && 'displayName' in val) return (val as SpeakerLinkMapEntry).displayName || speaker;
    return speaker;
}

function avatarFor(name: string, participants: TranscriptParticipant[]): string | null {
    return participants.find(p => p.name === name)?.avatar ?? null;
}

function initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (parts[0]?.slice(0, 2) || '').toUpperCase();
}

export default function TranscriptBubbles({
    segments,
    map,
    participants,
    className,
    onParticipantClick,
}: {
    segments: TranscriptSegment[];
    map?: SimpleAssignMap | SpeakerLinkMap;
    participants: TranscriptParticipant[];
    className?: string;
    onParticipantClick?: (participant: TranscriptParticipant) => void;
}) {
    // Stable left/right assignment per displayed name
    const sideMap: Record<string, 'left' | 'right'> = {};
    let next: 'left' | 'right' = 'left';
    const sideFor = (name: string) => {
        if (!sideMap[name]) { sideMap[name] = next; next = next === 'left' ? 'right' : 'left'; }
        return sideMap[name];
    };

    try {
        // Diagnostic logs to trace rendering problems
        const uniqNames = Array.from(new Set(segments.map(s => resolveDisplay(s.speaker, map))));
        console.debug('[TranscriptBubbles] render', {
            segments: segments.length,
            participants: participants.length,
            uniqueNames: uniqNames,
            mapKeys: map ? Object.keys(map as any).length : 0,
            sample: segments.slice(0, 3),
        });
    } catch { /* ignore logging errors */ }

    return (
        <div className={className ? `space-y-2 ${className}` : 'space-y-2'}>
            {segments.map((s, idx) => {
                const display = resolveDisplay(s.speaker, map);
                const side = sideFor(display);
                const avatar = avatarFor(display, participants);
                const participant = participants.find(p => p.name === display);
                const bubble = (
                    <div className={`inline-block px-3 py-2 rounded-2xl text-sm sm:max-w-[80%] max-w-full ${side === 'left' ? 'bg-gray-800 text-gray-100 rounded-bl-sm' : 'bg-blue-600/90 text-white rounded-br-sm'} w-auto sm:w-auto`} style={{ wordBreak: 'break-word' }}>
                        <div className="text-[11px] opacity-75 mb-1">{display}</div>
                        <div className="whitespace-pre-wrap leading-5">{s.text}</div>
                    </div>
                );
                return (
                    <div key={idx} className={`flex items-center gap-2 ${side === 'left' ? 'justify-start' : 'justify-end'} sm:px-0 px-1`}>
                        {side === 'left' && (
                            onParticipantClick && participant?.contactId ? (
                                <button
                                    onClick={() => onParticipantClick(participant)}
                                    className="w-7 h-7 rounded-full bg-gray-700 overflow-hidden grid place-items-center text-[11px] text-gray-200 hover:ring-2 hover:ring-blue-500 cursor-pointer transition-all"
                                    title="Click to view contact profile"
                                >
                                    {avatar ? <img src={avatar} className="w-full h-full object-cover" /> : <span>{initials(display)}</span>}
                                </button>
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-gray-700 overflow-hidden grid place-items-center text-[11px] text-gray-200">
                                    {avatar ? <img src={avatar} className="w-full h-full object-cover" /> : <span>{initials(display)}</span>}
                                </div>
                            )
                        )}
                        {bubble}
                        {side === 'right' && (
                            onParticipantClick && participant?.contactId ? (
                                <button
                                    onClick={() => onParticipantClick(participant)}
                                    className="w-7 h-7 rounded-full bg-gray-700 overflow-hidden grid place-items-center text-[11px] text-gray-200 hover:ring-2 hover:ring-blue-500 cursor-pointer transition-all"
                                    title="Click to view contact profile"
                                >
                                    {avatar ? <img src={avatar} className="w-full h-full object-cover" /> : <span>{initials(display)}</span>}
                                </button>
                            ) : (
                                <div className="w-7 h-7 rounded-full bg-gray-700 overflow-hidden grid place-items-center text-[11px] text-gray-200">
                                    {avatar ? <img src={avatar} className="w-full h-full object-cover" /> : <span>{initials(display)}</span>}
                                </div>
                            )
                        )}
                    </div>
                );
            })}
        </div>
    );
}
